import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { createAgentur, getAgenturenByOrganization, deleteAgentur, updateAgenturData, getTeamsByOrganization } from '../../api';
import CreateAgenturDialog from '../common/CreateAgenturDialog';
import { useNavigate, useLocation } from 'react-router-dom';
import CreateUser from '../../pages/CreateUser';
import AgenturDashboard from './AgenturDashboard'; // Added import for AgenturDashboard
import DashboardNav from './DashboardNav';
import { FaCog, FaTrash } from 'react-icons/fa';

function AgenturenVerwalten({ user }) {
  const [agenturen, setAgenturen] = useState([]);
  const [selectedAgentur, setSelectedAgentur] = useState(null); // NEU
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [addUserAgenturId, setAddUserAgenturId] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [agenturToEdit, setAgenturToEdit] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', beschreibung: '', price_pv: '', price_wp: '', price_pv_wp: '' });
  const [orgTeamsForEdit, setOrgTeamsForEdit] = useState([]);
  const [orgTeamsForEditLoading, setOrgTeamsForEditLoading] = useState(false);
  const [selectedAllowedTeamIds, setSelectedAllowedTeamIds] = useState([]);
  const [projectsDropdownOpen, setProjectsDropdownOpen] = useState(false);
  const projectsDropdownRef = useRef(null);
  const projectsDropdownButtonRef = useRef(null);
  const [projectsPanelLayout, setProjectsPanelLayout] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agenturToDelete, setAgenturToDelete] = useState(null);

  // URL-Parameter auslesen
  const urlParams = new URLSearchParams(location.search);
  const orgIdFromUrl = urlParams.get('orgId');
  const targetOrgId = orgIdFromUrl || user?.organization_id;

  // Responsive-Logik wie in TeamManagement.js
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Agenturen laden
  const loadAgenturen = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAgenturenByOrganization(targetOrgId);
      if (data.success) {
        setAgenturen(data.agenturen || []);
      } else {
        setError(data.error || 'Fehler beim Laden der Agenturen.');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (targetOrgId) {
      loadAgenturen();
    }
    // eslint-disable-next-line
  }, [targetOrgId]);

  useEffect(() => {
    if (!editDialogOpen || !user?.id || !targetOrgId) return;
    let cancelled = false;
    setOrgTeamsForEditLoading(true);
    getTeamsByOrganization(user.id, targetOrgId).then((res) => {
      if (cancelled) return;
      setOrgTeamsForEdit(res.success ? res.teams || [] : []);
      setOrgTeamsForEditLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [editDialogOpen, user?.id, targetOrgId]);

  useEffect(() => {
    if (!projectsDropdownOpen) return;
    const close = (e) => {
      if (projectsDropdownRef.current && !projectsDropdownRef.current.contains(e.target)) {
        setProjectsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [projectsDropdownOpen]);

  const updateProjectsPanelLayout = () => {
    const el = projectsDropdownButtonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 16;
    const spaceAbove = rect.top - 16;
    const desiredMax = 360;
    let openDown = true;
    let maxHeight = Math.min(desiredMax, Math.max(140, spaceBelow));
    if (spaceBelow < 160 && spaceAbove > spaceBelow) {
      openDown = false;
      maxHeight = Math.min(desiredMax, Math.max(140, spaceAbove - 8));
    }
    const width = Math.max(rect.width, 220);
    const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));
    setProjectsPanelLayout({
      top: openDown ? rect.bottom + 4 : Math.max(8, rect.top - maxHeight - 4),
      left,
      width,
      maxHeight,
    });
  };

  useLayoutEffect(() => {
    if (!projectsDropdownOpen) {
      setProjectsPanelLayout(null);
      return;
    }
    updateProjectsPanelLayout();
  }, [projectsDropdownOpen, orgTeamsForEdit.length]);

  useEffect(() => {
    if (!projectsDropdownOpen) return;
    const handler = () => updateProjectsPanelLayout();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [projectsDropdownOpen]);

  // Wenn Agenturen geladen wurden und noch keine ausgewählt ist, Standard setzen
  useEffect(() => {
    if (agenturen.length > 0 && !selectedAgentur) {
      setSelectedAgentur(agenturen[0]);
    }
  }, [agenturen, selectedAgentur]);

  const handleCreateAgentur = async (agenturData) => {
    setDialogLoading(true);
    setDialogError('');
    try {
      const result = await createAgentur(agenturData.name, agenturData.beschreibung, targetOrgId);
      if (result.success) {
        setShowDialog(false);
        loadAgenturen();
      } else {
        setDialogError(result.error || 'Fehler beim Erstellen der Agentur.');
      }
    } catch (err) {
      setDialogError('Netzwerkfehler: ' + err.message);
    } finally {
      setDialogLoading(false);
    }
  };

  // Handler für Bearbeiten
  const handleEditClick = (agentur) => {
    setAgenturToEdit(agentur);
    setEditForm({
      name: agentur.name,
      beschreibung: agentur.beschreibung || '',
      price_pv: agentur.price_pv != null ? String(agentur.price_pv) : '',
      price_wp: agentur.price_wp != null ? String(agentur.price_wp) : '',
      price_pv_wp: agentur.price_pv_wp != null ? String(agentur.price_pv_wp) : '',
    });
    setSelectedAllowedTeamIds(
      Array.isArray(agentur.allowed_team_ids)
        ? agentur.allowed_team_ids.map((id) => Number(id)).filter((id) => id > 0)
        : []
    );
    setProjectsDropdownOpen(false);
    setEditDialogOpen(true);
  };

  const toggleAllowedTeam = (teamId) => {
    const id = Number(teamId);
    if (!id) return;
    setSelectedAllowedTeamIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async () => {
    if (!agenturToEdit || !editForm.name.trim()) return;
    setDialogLoading(true);
    setDialogError('');
    try {
      const allowedIds = selectedAllowedTeamIds.map((id) => Number(id)).filter((id) => id > 0);
      const res = await updateAgenturData(user.id, agenturToEdit.id, {
        name: editForm.name.trim(),
        beschreibung: editForm.beschreibung.trim(),
        price_pv: editForm.price_pv === '' ? null : editForm.price_pv,
        price_wp: editForm.price_wp === '' ? null : editForm.price_wp,
        price_pv_wp: editForm.price_pv_wp === '' ? null : editForm.price_pv_wp,
        allowed_team_ids: allowedIds,
      });
      if (res.success) {
        setEditDialogOpen(false);
        setAgenturToEdit(null);
        await loadAgenturen();
      } else {
        setDialogError(res.error || 'Fehler beim Speichern.');
      }
    } catch (err) {
      setDialogError('Netzwerkfehler: ' + err.message);
    } finally {
      setDialogLoading(false);
    }
  };

  // Handler für Löschen
  const handleDeleteClick = async (agentur) => {
    if (window.confirm(`Möchten Sie die Agentur "${agentur.name}" wirklich löschen?`)) {
      setLoading(true);
      setError('');
      try {
        const result = await deleteAgentur(user.id, agentur.id);
        if (result.success) {
          await loadAgenturen();
        } else {
          setError(result.error || 'Fehler beim Löschen der Agentur.');
        }
      } catch (err) {
        setError('Netzwerkfehler: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Styles für das Grid/Karten-Layout wie in TeamManagement.js
  const styles = {
    container: {
      margin: '0 auto',
      maxWidth: '1400px',
      padding: isMobile ? '5px' : '15px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: '15px',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '10px' : '0',
    },
    title: {
      color: '#2d3748',
      fontSize: isMobile ? '1.2rem' : '1.6rem',
      margin: 0,
    },
    createBtn: {
      background: '#3182ce',
      color: '#fff',
      border: 'none',
      borderRadius: '5px',
      padding: isMobile ? '5px 10px' : '6px 14px', // Weniger Padding
      fontSize: isMobile ? '0.9rem' : '1rem', // Etwas kleiner
      cursor: 'pointer',
      marginBottom: isMobile ? '10px' : '0',
      minWidth: 'unset', // Keine Mindestbreite
      width: 'auto', // Automatische Breite
      fontWeight: 500,
      lineHeight: 1.2,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      gap: isMobile ? '12px' : '16px',
      marginBottom: '20px',
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      padding: isMobile ? '16px' : '20px',
      border: '1px solid #e2e8f0',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      minHeight: '140px',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px',
    },
    agenturName: {
      fontSize: isMobile ? '1rem' : '1.1rem',
      fontWeight: '600',
      color: '#2d3748',
      margin: 0,
      lineHeight: '1.3',
      flex: 1,
    },
    beschreibung: {
      color: '#4a5568',
      fontSize: isMobile ? '0.85rem' : '1rem',
      marginBottom: '10px',
      minHeight: '32px',
    },
    cardActions: {
      display: 'flex',
      gap: '8px',
      marginTop: 'auto',
      flexWrap: 'wrap',
    },
    actionBtn: {
      background: '#3182ce',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      padding: isMobile ? '6px 10px' : '8px 12px',
      fontSize: isMobile ? '0.7rem' : '0.8rem',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'background-color 0.2s',
      minWidth: 'fit-content',
    },
    actionBtnSecondary: {
      background: '#38a169',
      color: 'white',
    },
    actionBtnTertiary: {
      background: '#e53e3e',
      color: 'white',
    },
    loading: {
      textAlign: 'center',
      padding: '20px',
      color: '#4a5568',
      fontSize: '0.9rem',
    },
    empty: {
      textAlign: 'center',
      padding: '20px',
      color: '#718096',
      fontSize: '0.9rem',
      background: 'white',
      borderRadius: '12px',
      border: '2px dashed #e2e8f0',
    },
  };

  // Navigation wie in TeamManagement.js
  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'agenturen') {
      navigate('/agenturen');
    }
  };

  return (
    <>
      <DashboardNav
        activeSection="agenturen"
        onSectionChange={handleSectionChange}
        user={user}
      />
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Agenturen verwalten</h2>
          <button style={styles.createBtn} onClick={() => setShowDialog(true)}>+ Agentur erstellen</button>
        </div>
        {loading ? (
          <div style={styles.loading}>Lade Agenturen...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <div style={styles.grid}>
            {agenturen.length === 0 ? (
              <div style={styles.empty}>Keine Agenturen gefunden</div>
            ) : (
              agenturen.map(ag => (
                <div key={ag.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <h4 style={styles.agenturName}>{ag.name}</h4>
                    <div style={{ display: 'flex', gap: '8px', position: 'absolute', top: 16, right: 16 }}>
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                        }}
                        title="Bearbeiten"
                        onClick={() => handleEditClick(ag)}
                      >
                        <FaCog size={18} color="#3182ce" />
                      </button>
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                        }}
                        title="Löschen"
                        onClick={() => handleDeleteClick(ag)}
                      >
                        <FaTrash size={18} color="#e53e3e" />
                      </button>
                    </div>
                  </div>
                  <div style={styles.beschreibung}>{ag.beschreibung}</div>
                  <div style={styles.cardActions}>
                    <button style={styles.actionBtn} onClick={() => navigate(`/agentur-dashboard/${ag.id}`)}>Dashboard</button>
                    <button style={{...styles.actionBtn, ...styles.actionBtnSecondary}} onClick={() => navigate(`/agenturen/${ag.id}/mitglieder`)}>Mitglieder</button>
                    <button style={{...styles.actionBtn, ...styles.actionBtnTertiary}} onClick={() => navigate(`/agenturen/${ag.id}/leads`)}>Leads</button>
                    <button style={{...styles.actionBtn, background: '#805ad5', color: 'white'}} onClick={() => navigate(`/agenturen/${ag.id}/teams`)}>Teams</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {/* Dialog für Benutzer hinzufügen */}
        {addUserAgenturId && (
          <div className="modal-backdrop">
            <div className="modal">
              <CreateUser
                user={user}
                onClose={() => setAddUserAgenturId(null)}
                preselectedOrganizationId={addUserAgenturId}
              />
            </div>
          </div>
        )}
        <CreateAgenturDialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
          onCreate={handleCreateAgentur}
          loading={dialogLoading}
          error={dialogError}
        />
        {/* Bearbeiten-Dialog */}
        {editDialogOpen && (
          <div className="modal-backdrop">
            <div className="modal">
              <h2>Agentur bearbeiten</h2>
              <div className="form-group">
                <label>Agenturname *</label>
                <input
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditChange}
                  placeholder="Name der Agentur"
                  required
                />
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  name="beschreibung"
                  value={editForm.beschreibung}
                  onChange={handleEditChange}
                  placeholder="Beschreibung (optional)"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Zugängliche Organisation-Projekte (Teams)</label>
                <p style={{ fontSize: '0.85rem', color: '#718096', margin: '0 0 8px 0' }}>
                  Dropdown öffnen und Projekte anhaken. Darunter folgen die Leadpreise – die Liste klappt nach unten zum Fensterrand und ist darin scrollbar.
                </p>
                {orgTeamsForEditLoading ? (
                  <div style={{ fontSize: '0.9rem', color: '#4a5568' }}>Projekte werden geladen…</div>
                ) : orgTeamsForEdit.length === 0 ? (
                  <div style={{ fontSize: '0.9rem', color: '#c05621' }}>Keine Teams in dieser Organisation.</div>
                ) : (
                  <div ref={projectsDropdownRef} style={{ position: 'relative', width: '100%' }}>
                    <button
                      ref={projectsDropdownButtonRef}
                      type="button"
                      onClick={() => setProjectsDropdownOpen((o) => !o)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        padding: '10px 12px',
                        textAlign: 'left',
                        fontSize: '1rem',
                        border: '1px solid #cbd5e0',
                        borderRadius: '6px',
                        background: '#fff',
                        cursor: 'pointer',
                        color: '#2d3748',
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedAllowedTeamIds.length === 0
                          ? 'Projekte auswählen…'
                          : selectedAllowedTeamIds.length === 1
                            ? orgTeamsForEdit.find(
                                (t) => Number(t.id) === selectedAllowedTeamIds[0]
                              )?.name ||
                              '1 Projekt'
                            : `${selectedAllowedTeamIds.length} Projekte ausgewählt`}
                      </span>
                      <span style={{ flexShrink: 0, color: '#718096', fontSize: '0.75rem' }}>
                        {projectsDropdownOpen ? '▲' : '▼'}
                      </span>
                    </button>
                    {projectsDropdownOpen && projectsPanelLayout && (
                      <div
                        style={{
                          position: 'fixed',
                          zIndex: 2000,
                          top: projectsPanelLayout.top,
                          left: projectsPanelLayout.left,
                          width: projectsPanelLayout.width,
                          maxHeight: projectsPanelLayout.maxHeight,
                          overflowY: 'auto',
                          WebkitOverflowScrolling: 'touch',
                          overscrollBehavior: 'contain',
                          border: '1px solid #cbd5e0',
                          borderRadius: '6px',
                          background: '#fff',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          padding: '6px 4px',
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {orgTeamsForEdit.map((t) => (
                          <label
                            key={t.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '8px 10px',
                              cursor: 'pointer',
                              fontSize: '0.95rem',
                              borderRadius: '4px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f7fafc';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedAllowedTeamIds.includes(Number(t.id))}
                              onChange={() => toggleAllowedTeam(t.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span>{t.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Leadkosten PV (€)</label>
                <input
                  type="number"
                  name="price_pv"
                  min="0"
                  step="0.01"
                  value={editForm.price_pv}
                  onChange={handleEditChange}
                  placeholder="z.B. 150"
                />
              </div>
              <div className="form-group">
                <label>Leadkosten WP (€)</label>
                <input
                  type="number"
                  name="price_wp"
                  min="0"
                  step="0.01"
                  value={editForm.price_wp}
                  onChange={handleEditChange}
                  placeholder="z.B. 200"
                />
              </div>
              <div className="form-group">
                <label>Leadkosten PV+WP (€)</label>
                <input
                  type="number"
                  name="price_pv_wp"
                  min="0"
                  step="0.01"
                  value={editForm.price_pv_wp}
                  onChange={handleEditChange}
                  placeholder="z.B. 300"
                />
              </div>
              {dialogError && <div className="error-message" style={{ marginBottom: '1rem', color: '#c53030' }}>{dialogError}</div>}
              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => { setEditDialogOpen(false); setDialogError(''); }}>Abbrechen</button>
                <button className="save-btn" onClick={handleEditSave} disabled={!editForm.name.trim() || dialogLoading}>{dialogLoading ? 'Speichern...' : 'Speichern'}</button>
              </div>
              <style jsx>{`
                .modal-backdrop {
                  position: fixed;
                  top: 0; left: 0; right: 0; bottom: 0;
                  background: rgba(0,0,0,0.3);
                  display: flex;
                  align-items: flex-start;
                  justify-content: center;
                  z-index: 1000;
                  overflow-y: auto;
                  overflow-x: hidden;
                  -webkit-overflow-scrolling: touch;
                  padding: 24px 12px 48px;
                  box-sizing: border-box;
                }
                .modal {
                  background: #fff;
                  border-radius: 8px;
                  padding: 2rem;
                  min-width: 320px;
                  max-width: 95vw;
                  box-shadow: 0 4px 24px rgba(0,0,0,0.15);
                  margin: 0 auto;
                  overflow: visible;
                }
                .form-group {
                  margin-bottom: 1.2rem;
                }
                .form-group label {
                  display: block;
                  margin-bottom: 0.3rem;
                  font-weight: 500;
                }
                .form-group input,
                .form-group textarea {
                  width: 100%;
                  padding: 8px 10px;
                  border: 1px solid #cbd5e0;
                  border-radius: 5px;
                  font-size: 1rem;
                }
                .modal-actions {
                  display: flex;
                  justify-content: flex-end;
                  gap: 1rem;
                }
                .cancel-btn, .save-btn {
                  padding: 8px 18px;
                  border: none;
                  border-radius: 5px;
                  font-size: 1rem;
                  cursor: pointer;
                }
                .cancel-btn {
                  background: #e2e8f0;
                }
                .save-btn {
                  background: #3182ce;
                  color: #fff;
                }
                .save-btn:hover {
                  background: #2563eb;
                }
              `}</style>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AgenturenVerwalten; 